import React, { useEffect, useRef } from "react";
import "../assets/styles/Project.scss";

interface ProjectItem {
  title: string;
  description: string;
  link: string;
  tech: string[];
}

function Project() {
  const projectRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    projectRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const projects: ProjectItem[] = [
    {
      title: "FormSync (Ongoing) – End-to-End Strength Training Analyzer",
      link: "https://github.com/2002utkarsh",
      description:
        "A Garmin-mentored capstone project building an end-to-end system for real-time form analysis. The embedded wearable uses IMU sensors to stream motion data over BLE to a React Native app, enabling live trajectory analysis and performance feedback.",
      tech: ["C", "BLE", "React Native", "Python", "CI/CD"],
    },
    {
      title: "High-Performance Trading & Backtesting Engine",
      link: "https://github.com/2002utkarsh/stock_backtracing",
      description:
        "Built a multi-threaded C++ backtesting engine achieving a throughput of over 160,000 events/second on 1.6M historical data points. Engineered a fully automated Python pipeline to ingest live market data from the KuCoin API, generate signals, and execute orders for autonomous trading",
      tech: ["C++", "Python", "KuCoin API", "Docker"],
    },
    {
      title: "GateKepper",
      link: "https://github.com/2002utkarsh/AI-Enhanced-Obituaries-Application",
      description:
        "A modular, command-line API security scanner built with Java 17 and picocli to detect critical vulnerabilities, including insecure CORS and SQLi. The tool is containerized using Docker multi-stage builds and integrated into GitHub Actions and Jenkins CI/CD pipelines to automatically fail builds on vulnerability detection.",
      tech: ["Java", "Jenkins", "Github Actions"],
    },

    {
      title: "AI-Enhanced Obituaries Platform",
      link: "https://github.com/2002utkarsh/AI-Enhanced-Obituaries-Application",
      description:
        "Developed a serverless AWS application using OpenAI GPT, Amazon Polly, and Cloudinary to generate personalized AI-written obituaries with narration and image handling. Deployed using Terraform with a scalable CI/CD pipeline.",
      tech: ["AWS", "OpenAI API", "Amazon Polly", "Terraform", "CI/CD", "Serverless"],
    },
    {
      title: "Flight booking System",
      link: "https://github.com/brianmeta/ENSF480Implementation",
      description:
        "A full-stack flight booking platform architected with Node.js and Java. It uses Observer and Strategy design patterns to power real-time seat notifications and flexible payment methods, while a multi-table relational schema enforces 5-level role-based access control to secure booking operations.",
      tech: ["Java", "MySQL"],
    },
    {
      title: "Intelligent Team Workspace",
      link: "https://github.com/ahmeda110/project-managment-system-401",
      description:
        "A collaborative project platform built with React and Supabase, featuring role-based authentication and real-time Kanban boards. It ensures client state synchronization for live chat and dashboards, and includes an intelligent dashboard that visualizes team velocity and proactively identifies at-risk tasks with a predictive model.",
      tech: ["React", "JavaScript", "AWS Cognito", "AWS S3", "Local Storage"],
    },
    {
      title: "Full-Stack Note-Taking App",
      link: "https://github.com/2002utkarsh/Notes-Taking-App",
      description:
        "Engineered a full-stack, serverless note-taking application inspired by Notion's architecture. Developed a responsive front-end with React and leveraged AWS services for the back-end, using AWS Cognito for secure user authentication and AWS S3 for data persistence. Implemented browser local storage to provide offline-first note-taking capabilities.",
      tech: ["React", "JavaScript", "AWS Cognito", "AWS S3", "Local Storage"],
    },
    {
      title: "Scheduler",
      link: "https://github.com/2002utkarsh/Pet-Medical-Scheduler",
      description:
        "Engineered a multi-user, thread-safe scheduling system in Java, implementing concurrent controls to prevent race conditions. Cut event conflict-detection latency by 95% by replacing a linear scan with a TreeMap, reducing the operation's time complexity from O(N) to O(logN).",
      tech: ["Java", "Junit"],
    },
    // {
    //   title: "Linux-Based Job Scheduling Algorithm",
    //   link: "https://github.com/2002utkarsh/Scheduler-in-Linux",
    //   description:
    //     "Engineered a multi-algorithm process scheduler in C to analyze the performance trade-offs of core operating system strategies on Linux. Implemented and benchmarked classic algorithms including FIFO, SJF, Round Robin, and STCF, measuring key metrics like response time and throughput under various workloads to evaluate their effectiveness.",
    //   tech: ["C", "Linux", "Shell"],
    // },
    // {
    //   title: "Java Data Structures & Algorithms Library",
    //   link: "https://github.com/2002utkarsh/Java-Library-For-Data-Structures",
    //   description:
    //     "Developed a comprehensive Java library of fundamental data structures and algorithms with a strict focus on clean API design and engineering best practices. Ensured code correctness and reliability with thorough unit and boundary-case testing for all modules.",
    //   tech: ["Java", "JUnit"],
    // },
  ];

  return (
    <div className="projects-container" id="projects">
      <h1>Projects</h1>

      <div className="projects-grid">
        {projects.map((proj, i) => (
          <div
            key={i}
            className={`project fade-up delay-${i}`}
            ref={(el) => (projectRefs.current[i] = el)}
          >
            <a href={proj.link} target="_blank" rel="noopener noreferrer">
              <h2>{proj.title}</h2>
            </a>
            <p>{proj.description}</p>

            <div className="flex-chips">
              <span className="chip-title">Tech Stack:</span>
              {proj.tech.map((t, index) => (
                <span key={index} className="chip">
                  {t}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default Project;
